<template>
  <div ref="treeContainerRef" class="folder-picker-tree">
    <div v-if="allowRoot" class="nav-tree-item">
      <button
        type="button"
        class="nav-link folder-link"
        style="padding-left: 12px"
        :class="{ active: selectedId === 'root' }"
        @click="selectRoot"
      >
        <span class="caret-placeholder mr-1"></span>
        <i class="fas fa-home mr-2"></i>
        <span class="text-truncate">{{ rootLabel }}</span>
      </button>
    </div>
    <folder-node
      v-for="folder in topLevelFolders"
      :key="folder.id"
      :folder="folder"
      :get-children="getChildren"
      :is-collapsed="isCollapsed"
      :toggle-folder="toggleFolder"
      :select-folder="selectFolder"
      :selected-folder-id="selectedId"
      :allowed-folder-ids="allowedFolderIds"
      :depth="1"
    ></folder-node>
    <div class="folder-picker-tree-empty text-muted small" v-if="!topLevelFolders.length">
      {{ emptyLabel }}
    </div>
    <input type="hidden" :name="parentInputName" :value="parentInputValue">
    <input v-if="depthInputName" type="hidden" :name="depthInputName" :value="depthValue">
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import FolderNode from './FolderNode.vue';
import { useFolderTree } from '../dashboard/useFolderTree';

const props = defineProps({
  folders: {
    type: Array,
    required: true,
  },
  initialParentId: {
    type: [Number, String, null],
    default: null,
  },
  allowRoot: {
    type: Boolean,
    default: false,
  },
  rootLabel: {
    type: String,
    default: '',
  },
  emptyLabel: {
    type: String,
    default: '',
  },
  parentInputName: {
    type: String,
    default: 'parent_id',
  },
  depthInputName: {
    type: String,
    default: '',
  },
  allowedFolderIds: {
    type: [Array, null],
    default: null,
  },
});

const treeContainerRef = ref(null);

// '' (未選択) | フォルダid(数値) | 'root' (TOP・クライアント内部の sentinel)
const selectedId = ref('');

const { topLevelFolders, getChildren, isCollapsed, toggleFolder, expandAncestorsOf } = useFolderTree(
  computed(() => props.folders),
  selectedId,
  { persist: false, initialCollapse: 'collapseAll' }
);

const depthValue = computed(() => {
  if (selectedId.value === 'root') {
    return 1;
  }
  if (typeof selectedId.value === 'number') {
    const folder = props.folders.find((f) => String(f.id) === String(selectedId.value));
    return folder ? Number(folder.depth) + 1 : '';
  }
  return '';
});

const parentInputValue = computed(() => (typeof selectedId.value === 'number' ? String(selectedId.value) : ''));

const selectFolder = (folderId) => {
  const parsed = Number(folderId);
  if (!Number.isFinite(parsed)) {
    return;
  }
  selectedId.value = parsed;
};

const selectRoot = () => {
  selectedId.value = 'root';
};

onMounted(async () => {
  const parsed = Number(props.initialParentId);
  const hasValidInitialParent =
    props.initialParentId !== null && props.initialParentId !== '' && Number.isFinite(parsed) && parsed > 0;

  if (hasValidInitialParent) {
    selectedId.value = parsed;
    await nextTick();
    expandAncestorsOf(parsed);
    // 展開による子ノード(FolderNode.vue の v-if="children.length && !collapsed")のDOM描画確定を待つ
    await nextTick();
    // ページ全体を巻き込む scrollIntoView は使わず、テンプレート ref のツリーボックス内部のみスクロールする
    const container = treeContainerRef.value;
    const target = container?.querySelector(`.folder-link[data-folder-id="${parsed}"]`);
    if (container && target) {
      container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    }
    return;
  }

  if (props.allowRoot) {
    selectedId.value = 'root';
  }
});
</script>
